import { AppType } from "client/Api/Apps/common";
import {
	Button,
	FormItem,
	InputRow,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { ZaaksysteemConnectionDialog } from "client/Features/Settings/Connections/Zaaksysteem/ZaaksysteemConnectionDialog";
import { ZaaksysteemConnectionListItemModel } from "client/Api/ZaaksysteemConfiguration/zaaksysteemConfiguration";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	ZaaksysteemConnectionSelectOptionsModel,
	getZaaksysteemConnections,
	ZaaksysteemConnectionSelectOptionModel
} from "client/Api/Apps/EditApp/appParameters";
import {
	SaveZaaksysteemAppModel,
	EditZaaksysteemAppViewModel,
	saveZaaksysteemApp
} from "client/Api/Apps/EditApp/saveApp";

const validation = createValidationConfiguration<SaveZaaksysteemAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("connectionId")
	.add(
		isNotNullNorUndefined()
	);

export type EditZaaksysteemAppProps = {
	initialSettings: EditAppSettings<EditZaaksysteemAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditZaaksysteemApp = forwardRef(
	(
		props: EditZaaksysteemAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState<boolean>(false);
		const [connections, setConnections] = useState<ZaaksysteemConnectionSelectOptionsModel>({
			canCreateNew: false,
			options: []
		});

		const [formData, setFormData] = useState<SaveZaaksysteemAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const connectionOptions: readonly SelectItem<number>[] = useMemo(
			(): readonly SelectItem<number>[] => {
				return connections.options
					.map(
						o => ({
							value: o.id,
							text: o.name
						})
					);
			},
			[connections.options]
		);

		const alertManager: AlertManager = useAlerts();

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveZaaksysteemAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const addConnectionHandler = useCallback(
			(item: ZaaksysteemConnectionListItemModel): void => {
				setConnections((newConnections: ZaaksysteemConnectionSelectOptionsModel) => {
					const existingItemIndex: number = newConnections.options
						.findIndex(i => i.id === item.id);

					const options: ZaaksysteemConnectionSelectOptionModel[] = [
						...newConnections.options
					];

					if (existingItemIndex === -1) {
						options.push(item);
					} else {
						options.splice(existingItemIndex, 1, item);
					}

					return {
						...newConnections,
						options
					};
				});

				formHandler.for("connectionId")
					.attachToSelect()
					.onChange(item.id);
			},
			[formHandler]
		);

		useEffect(
			() => {
				const loadZaaksysteemConnections = async (
					signal: AbortSignal
				): Promise<void> => {
					try {
						const result: ZaaksysteemConnectionSelectOptionsModel =
							await getZaaksysteemConnections(signal);

						setConnections(result);
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}
						alertManager.showError(localize("_GeneralErrorMessage"));
					}
				};

				const cancellation = new AbortController();
				loadZaaksysteemConnections(cancellation.signal);

				return () => {
					cancellation.abort();
				};
			},
			[alertManager]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveZaaksysteemAppModel>(
			formHandler,
			saveZaaksysteemApp,
			AppType.Zaaksysteem,
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connectionId", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<>
				<EditAppBase
					formHandler={formHandler}
					hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
					initialAppSettings={app}
					usersAndUserGroupsData={usersAndUserGroupsData}
					initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
				>
					<FormItem label={localize("_Connection")}>
						<InputRow>
							<InputRow.Stretch>
								<Select
									{...formHandler.for("connectionId")
										.attachToSelect()}
									options={connectionOptions}
									placeholder={localize("_SelectConnection")}
									menuTitle={localize("_Connection")}
								/>
							</InputRow.Stretch>
							{connections.canCreateNew && (
								<Button
									text={localize("_Add")}
									onClick={() => setIsConnectionDialogOpen(true)}
									kind="secondary"
								/>
							)}
						</InputRow>
						<ValidationError
							{...formHandler.for("connectionId")
								.attachToValidationError()}
						/>
					</FormItem>
					<AppDestinationWindowSelectFormItem
						attachable={formHandler.for("destinationWindow")}
					/>
				</EditAppBase>
				{isConnectionDialogOpen && (
					<ZaaksysteemConnectionDialog
						onAfterSave={addConnectionHandler}
						onClose={() => setIsConnectionDialogOpen(false)}
					/>
				)}
			</>
		);
	}
);

EditZaaksysteemApp.displayName = "EditZaaksysteemApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditZaaksysteemAppViewModel>
): SaveZaaksysteemAppModel {
	const {
		connectionId,
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		connectionId,
		destinationWindow,
	};
}